import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  type StyleTypeMetadata,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn, ISiteTextPreset } from '@wix/yoshi-flow-editor';
import {
  BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID,
  BOOKINGS_MAIN_PAGE_PRESET_ID,
  CLASSIC_EDITOR_X_PRESET_ID,
  CLASSIC_PRESET_ID,
  GRID_EDITOR_X_PRESET_ID,
  GRID_PRESET_ID,
  SINGLE_SERVICE_EDITOR_X_PRESET_ID,
  SINGLE_SERVICE_PRESET_ID,
  STRIP_EDITOR_X_PRESET_ID,
  STRIP_PRESET_ID,
} from '../../consts';
import {
  AlignmentOptions,
  ImageAndTextRatioOptions,
  ImagePositionOptions,
  ImageShapeOptions,
  ImageSizeOptions,
  ServiceListLayoutOptions,
} from '../../types/types';
import {
  mapNumberToPresetId,
  presetIdFromSettings,
} from '../../utils/settings/settings';
import type { VisibilityKey } from './hooks/types';
import {
  generateAllCssVars,
  getIsServiceImageVisibleFromTpaDate,
} from './styleParams-utils';

const getPresetId = <F extends Function>(getStyleParamValue: F) => {
  let preset = getStyleParamValue(presetId);

  if (!preset && presetIdFromSettings) {
    preset = presetIdFromSettings;
  }

  if (typeof preset === 'number') {
    preset = mapNumberToPresetId(preset);
  }

  return preset;
};

type CssColorValue = Parameters<typeof wixColor>[0];
type CssFontValue = Parameters<typeof wixFont>[0];

export type stringStyleParams<T extends string = string> = {
  fontStyleParam: boolean;
  value: T;
};

export const buildStringStyleParams = <T extends string = string>(
  value: T,
): stringStyleParams<T> => {
  return { fontStyleParam: false, value };
};

export const getStringStyleParamsValue = (
  stringStyleParamObject: stringStyleParams,
) => {
  return stringStyleParamObject?.value;
};

export enum IStylesParamsColorsKeys {
  widgetBackgroundColor = 'widgetBackgroundColor', // MULTI_OFFERINGS_BACKGROUND_COLOR
  widgetTitleColor = 'widgetTitleColor', // MULTI_OFFERINGS_TITLE_COLOR
  infoBackgroundColor = 'infoBackgroundColor', // BACKGROUND_COLOR
  borderColor = 'borderColor', // BORDER_COLOR
  filterTitleColor = 'filterTitleColor', // CATEGORY_NAME_COLOR
  filterSelectedIndicationColor = 'filterSelectedIndicationColor', // CATEGORY_SELECTED_UNDERLINE_COLOR
  filterSelectedTextColor = 'filterSelectedTextColor', // CATEGORY_SELECTED_TEXT_COLOR
  filterSelectedBorderColor = 'filterSelectedBorderColor', // CATEGORY_SELECTED_BORDER_COLOR
  filterHoverIndicationColor = 'filterHoverIndicationColor', // CATEGORY_HOVER_BACKGROUND_COLOR
  filterHoverTextColor = 'filterHoverTextColor', // CATEGORY_HOVER_TEXT_COLOR
  filterHoverBorderColor = 'filterHoverBorderColor', // CATEGORY_HOVER_BORDER_COLOR
  filterBorderColor = 'filterBorderColor', // CATEGORY_DIVIDER_COLOR
  filterBackgroundColor = 'filterBackgroundColor', // CATEGORY_BACKGROUND_COLOR
  dropdownTextColor = 'dropdownTextColor', // MOBILE_CATEGORY_NAME_COLOR
  dropdownBorderColor = 'dropdownBorderColor', // MOBILE_CATEGORY_BORDER_COLOR
  dropdownBackgroundColor = 'dropdownBackgroundColor', // MOBILE_CATEGORY_BACKGROUND_COLOR
  serviceImageBackgroundColor = 'serviceImageBackgroundColor', // IMAGE_BACKGROUND_COLOR
  servicesDividerColor = 'servicesDividerColor', // SERVICE_DIVIDER_COLOR
  serviceDetailsColor = 'serviceDetailsColor', // OFFERING_DETAILS_COLOR
  courseAvailabilityColor = 'courseAvailabilityColor', // COURSE_AVAILABILITY_COLOR
  serviceNameColor = 'serviceNameColor', // OFFERING_NAME_COLOR
  serviceTaglineColor = 'serviceTaglineColor', // OFFERING_TAGLINE_COLOR
  moreInfoButtonColor = 'moreInfoButtonColor', // OFFERING_MORE_INFO_LABEL_COLOR
  serviceDividerColor = 'serviceDividerColor', // DIVIDER_COLOR
  bookButtonBackgroundColor = 'bookButtonBackgroundColor', // BUTTON_BACKGROUND_COLOR
  holeBookButtonTextColor = 'holeBookButtonTextColor', // BUTTON_TEXT_COLOR_HOLE
  fillBookButtonTextColor = 'fillBookButtonTextColor', // BUTTON_TEXT_COLOR_FILL
  bookButtonBorderColor = 'bookButtonBorderColor', // BUTTON_BORDER_COLOR
  bookButtonHoverBackgroundColor = 'bookButtonHoverBackgroundColor',
  bookButtonHoverBorderColor = 'bookButtonHoverBorderColor',
  bookButtonHoverTextColor = 'bookButtonHoverTextColor',
  explorePlansTextColor = 'explorePlansTextColor', // EXPLORE_PLANS_TEXT_COLOR
  fillLoadServicesButtonTextColor = 'fillLoadServicesButtonTextColor', // LOAD_SERVICES_BUTTON_TEXT_COLOR_FILL
  loadServicesButtonBackgroundColor = 'loadServicesButtonBackgroundColor', // LOAD_SERVICES_BUTTON_BACKGROUND_COLOR
  loadServicesButtonBorderColor = 'loadServicesButtonBorderColor', // LOAD_SERVICES_BUTTON_BORDER_COLOR
  widgetMenuTitleColor = 'widgetMenuTitleColor', // MENU_TITLE_COLOR
  priceAfterDiscountColor = 'priceAfterDiscountColor', // PRICE_AFTER_DISCOUNT_COLOR
  discountNameColor = 'discountNameColor', // DISCOUNT_NAME_COLOR
}

export enum IStylesParamsFontsKeys {
  widgetTitleFont = 'widgetTitleFont', // MULTI_OFFERINGS_TITLE_FONT
  filterTitleFont = 'filterTitleFont', // CATEGORY_NAME_FONT
  dropdownTextFont = 'dropdownTextFont', // MOBILE_CATEGORY_NAME_FONT
  serviceNameFont = 'serviceNameFont', // OFFERING_NAME_FONT
  courseAvailabilityFont = 'courseAvailabilityFont', // COURSE_AVAILABILITY_FONT
  serviceTagLineFont = 'serviceTagLineFont', // OFFERING_TAGLINE_FONT
  moreInfoButtonFont = 'moreInfoButtonFont', // OFFERING_MORE_INFO_LABEL_FONT
  serviceDetailsFont = 'serviceDetailsFont', // OFFERING_DETAILS_FONT
  priceAfterDiscountFont = 'priceAfterDiscountFont', // PRICE_AFTER_DISCOUNT_FONT
  discountNameFont = 'discountNameFont', // DISCOUNT_NAME_FONT
  bookButtonFont = 'bookButtonFont', // BUTTON_TEXT_FONT
  explorePlansFont = 'explorePlansFont', // EXPLORE_PLANS_FONT
  loadServicesButtonFont = 'loadServicesButtonFont', // LOAD_SERVICES_BUTTON_FONT
  serviceImageSize = 'serviceImageSize',
  serviceTextAlignment = 'serviceTextAlignment',
  titleAlignment = 'titleAlignment',
  serviceImagePosition = 'serviceImagePosition',
  serviceImageShape = 'serviceImageShape',
  serviceListLayout = 'serviceListLayout',
  serviceListMobileLayout = 'serviceListMobileLayout',
  menuTitleAlignment = 'menuTitleAlignment',
  widgetMenuTitleFont = 'widgetMenuTitleFont', // CATEGORY_TITLE_FONT
}

enum ILegacyStylesParamsFontsKeys {
  OFFERING_NAME_FONT = 'OFFERING_NAME_FONT',
  CATEGORY_NAME_FONT = 'CATEGORY_NAME_FONT',
  MULTI_OFFERINGS_TITLE_FONT = 'MULTI_OFFERINGS_TITLE_FONT',
  MOBILE_CATEGORY_NAME_FONT = 'MOBILE_CATEGORY_NAME_FONT',
  COURSE_AVAILABILITY_FONT = 'COURSE_AVAILABILITY_FONT',
  OFFERING_TAGLINE_FONT = 'OFFERING_TAGLINE_FONT',
  OFFERING_MORE_INFO_LABEL_FONT = 'OFFERING_MORE_INFO_LABEL_FONT',
  OFFERING_DETAILS_FONT = 'OFFERING_DETAILS_FONT',
  BUTTON_TEXT_FONT = 'BUTTON_TEXT_FONT',
  EXPLORE_PLANS_FONT = 'EXPLORE_PLANS_FONT',
}

export enum IStylesParamsNumbersKeys {
  mobileWidgetTitleFontSize = 'mobileWidgetTitleFontSize', // MOBILE_MULTI_OFFERINGS_TITLE_FONT_SIZE
  borderWidth = 'borderWidth', // BORDER_WIDTH
  cornerRadius = 'cornerRadius', // CORNER_RADIUS
  mobileFilterTitleFontSize = 'mobileFilterTitleFontSize', // MOBILE_CATEGORY_NAME_FONT_SIZE
  filterBorderWidth = 'filterBorderWidth', // CATEGORY_BORDER_WIDTH
  filterBorderRadius = 'filterBorderRadius', // CATEGORY_BORDER_RADIUS
  filterSpacing = 'filterSpacing', // CATEGORY_SPACING
  dropdownBorderWidth = 'dropdownBorderWidth', // MOBILE_CATEGORY_BORDER_WIDTH
  overlappingSidePadding = 'overlappingSidePadding', // OVERLAPPING_SIDE_PADDING
  overlappingVerticalPadding = 'overlappingVerticalPadding', // OVERLAPPING_VERTICAL_PADDING
  stripBorderWidth = 'stripBorderWidth', // STRIP_BORDER_WIDTH
  stripSidePadding = 'stripSidePadding', // STRIP_SIDE_PADDING
  stripVerticalPadding = 'stripVerticalPadding', // STRIP_VERTICAL_PADDING, MOBILE_STRIP_VERTICAL_PADDING
  imageAndTextRatio = 'imageAndTextRatio', // IMAGE_AND_TEXT_RATIO
  mobileServiceNameFontSize = 'mobileServiceNameFontSize', // MOBILE_OFFERING_NAME_FONT_SIZE
  mobileServiceTaglineFontSize = 'mobileServiceTaglineFontSize', // MOBILE_OFFERING_TAGLINE_FONT_SIZE
  mobileMoreInfoButtonFontSize = 'mobileMoreInfoButtonFontSize', // MOBILE_MORE_INFO_LABEL_FONT_SIZE
  serviceDividerWidth = 'serviceDividerWidth', // DIVIDER_WIDTH
  classicSidePadding = 'classicSidePadding', // CLASSIC_SIDE_PADDING, MOBILE_SIDE_PADDING
  classicVerticalPadding = 'classicVerticalPadding', // CLASSIC_VERTICAL_PADDING, MOBILE_VERTICAL_PADDING
  gridSidePadding = 'gridSidePadding', // GRID_SIDE_PADDING, MOBILE_SIDE_PADDING
  gridVerticalPadding = 'gridVerticalPadding', // GRID_VERTICAL_PADDING, MOBILE_VERTICAL_PADDING
  mobileServiceDetailsFontSize = 'mobileServiceDetailsFontSize', // MOBILE_OFFERING_DETAILS_FONT_SIZE
  mobileBookButtonFontSize = 'mobileBookButtonFontSize', // MOBILE_BUTTON_TEXT_FONT_SIZE
  bookButtonBorderWidth = 'bookButtonBorderWidth', // BUTTON_BORDER_WIDTH
  bookButtonCornerRadius = 'bookButtonCornerRadius', // BUTTON_CORNER_RADIUS
  serviceImageCornerRadius = 'serviceImageCornerRadius', // SERVICE_IMAGE_CORNER_RADIUS
  serviceImagePadding = 'serviceImagePadding', // SERVICE_IMAGE_PADDING
  mobileExplorePlansFontSize = 'mobileExplorePlansFontSize', // MOBILE_EXPLORE_PLANS_FONT_SIZE
  loadServicesButtonBorderRadius = 'loadServicesButtonBorderRadius', // LOAD_SERVICES_BUTTON_CORNER_RADIUS
  loadServicesButtonBorderWidth = 'loadServicesButtonBorderWidth', // LOAD_SERVICES_BUTTON_BORDER_WIDTH
  maxCardsPerRow = 'maxCardsPerRow',
  cardSpacing = 'cardSpacing',
  gridCardsSpacing = 'gridCardsSpacing',
  stripCardSpacing = 'stripCardSpacing',
  stripCardColumnsSpacing = 'stripCardColumnsSpacing',
  servciesDividerWidthInStrip = 'servciesDividerWidthInStrip',
  menuSectionsSpacing = 'menuSectionsSpacing',
  mobilewidgetMenuTitleFontSize = 'mobilewidgetMenuTitleFontSize',
  mobileDiscountPriceFontSize = 'mobileDiscountPriceFontSize',
  mobileDiscountNameFontSize = 'mobileDiscountNameFontSize',
  numberOfColumns = 'numberOfColumns',
}

export enum IStylesParamsBooleanKeys {
  isServiceImageVisible = 'isServiceImageVisible',
  isTitleVisible = 'isTitleVisible',
  isServiceDividerVisible = 'isServiceDividerVisible',
  isOnlineBadgeVisible = 'isOnlineBadgeVisible',
  isTagLineVisible = 'isTagLineVisible',
  isMoreInfoButtonVisible = 'isMoreInfoButtonVisible',
  isServiceOfferedDaysVisible = 'isServiceOfferedDaysVisible',
  isServiceStartDateVisible = 'isServiceStartDateVisible',
  isCourseAvailabilityVisible = 'isCourseAvailabilityVisible',
  isServiceDurationVisible = 'isServiceDurationVisible',
  isServicePriceVisible = 'isServicePriceVisible',
  isPriceAfterDiscountVisible = 'isPriceAfterDiscountVisible',
  isDiscountNameVisible = 'isDiscountNameVisible',
  isBookButtonVisible = 'isBookButtonVisible',
  isExplorePlansVisible = 'isExplorePlansVisible',
  alternateImageInfoSides = 'alternateImageInfoSides',
  showBreadcrumbs = 'showBreadcrumbs',
}

type IStylesParamsColors = {
  [key in `${IStylesParamsColorsKeys}`]: StyleParamType.Color;
};

type IStylesParamsFonts = {
  [key in `${IStylesParamsFontsKeys}`]: StyleParamType.Font;
};

type ILegacyStylesParamsFonts = {
  [key in `${ILegacyStylesParamsFontsKeys}`]: StyleParamType.Font;
};

type IStylesParamsNumbers = {
  [key in `${IStylesParamsNumbersKeys}`]: StyleParamType.Number;
};

type IStylesParamsBooleans = {
  [key in `${IStylesParamsBooleanKeys}`]: StyleParamType.Boolean;
};

const presetId = createStylesParam('presetId', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
});

export type IStylesParams = IStylesParamsColors &
  IStylesParamsFonts &
  ILegacyStylesParamsFonts &
  IStylesParamsNumbers &
  IStylesParamsBooleans & {
    presetId: StyleParamType.Number;
    bookButtonBorderRadius: StyleParamType.Number; // new settings corner radius
  };

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

const booleanMetadata = (defaultValue: boolean): StyleTypeMetadata => ({
  type: StyleType.booleanValue,
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const booleanParam = (defaultValue: boolean): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
  metadata: booleanMetadata(defaultValue),
});

const booleanParamWithUndefinedLegacy = (
  defaultValue: boolean,
): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => undefined as unknown as boolean,
  metadata: booleanMetadata(defaultValue),
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
  description?: string,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
    ...(description ? { ai: { description } } : {}),
  },
});

const numberParamWithMobileDefault = (
  type: StyleTypeFor<NumericValue>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

export const colorRefParam = (
  otherColorParam: ColorStyleParam,
  otherColorParamName: string,
  themeColor?: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherColorParam),
  metadata: {
    type: StyleType.color,
    defaultValue: themeColor
      ? wixColor(themeColor)
      : {
          type: 'ref',
          ref: otherColorParamName,
        },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  },
});

const legacyFontParam = (): Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
> => ({
  type: StyleParamType.Font,
});

const stringParam = <T extends string = string>(
  defaultValue?: T,
  description?: string,
): Omit<IStyleParam<StyleParamType.Font, ISettingsFont>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: () => buildStringStyleParams(defaultValue as T),
  metadata: {
    type: StyleType.UNKNOWN_CssPropertyType,
    defaultValue: {
      type: 'static',
      data: defaultValue as any,
    },
    ...(description ? { ai: { description } } : {}),
  },
});

export const customEnumParam = <T extends string = string>(
  defaultValue?: T,
  description?: string,
): Omit<IStyleParam<StyleParamType.Font, ISettingsFont>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: () => buildStringStyleParams(defaultValue as T),
  metadata: {
    type: StyleType.legacyFontStyleParamEnum,
    defaultValue: {
      type: 'static',
      value: defaultValue as any,
    },
    ...(description ? { ai: { description } } : {}),
  },
});

const mobileBookButtonFontSize = createStylesParam(
  'mobileBookButtonFontSize',
  numberParam(StyleType.fontSize, 14),
);

const bookButtonBorderColor = createStylesParam(
  'bookButtonBorderColor',
  colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstButtonPrimaryBorderBottomColor,
  ),
);

const bookButtonBackgroundColor = createStylesParam(
  'bookButtonBackgroundColor',
  colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstPrimaryBackgroundColor,
  ),
);

const fillBookButtonTextColor = createStylesParam(
  'fillBookButtonTextColor',
  colorParam(StyleType.color, 'color-1', CssColor.WstPrimaryBackgroundColor),
);

const stylesParams = createStylesParams<IStylesParams>({
  presetId: presetId as any,

  isServiceImageVisible: booleanParamWithUndefinedLegacy(true),
  isTitleVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue }) => {
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case BOOKINGS_MAIN_PAGE_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID:
          return true;
        default:
          return false;
      }
    },
    metadata: booleanMetadata(true),
  },
  isServiceDividerVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue, isMobile }) => {
      if (isMobile) {
        return true;
      }
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case SINGLE_SERVICE_PRESET_ID:
        case SINGLE_SERVICE_EDITOR_X_PRESET_ID:
          return false;
        default:
          return true;
      }
    },
    metadata: booleanMetadata(true),
  },
  isOnlineBadgeVisible: booleanParam(false),
  isPriceAfterDiscountVisible: booleanParam(true),
  isDiscountNameVisible: booleanParam(true),
  isTagLineVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue, isMobile }) => {
      if (isMobile) {
        return true;
      }
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
        case GRID_PRESET_ID:
        case GRID_EDITOR_X_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID:
        case SINGLE_SERVICE_PRESET_ID:
        case SINGLE_SERVICE_EDITOR_X_PRESET_ID:
          return false;
        default:
          return true;
      }
    },
    metadata: booleanMetadata(false),
  },
  isMoreInfoButtonVisible: booleanParam(false),
  isServiceOfferedDaysVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue, isMobile }) => {
      if (isMobile) {
        return true;
      }
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return false;
        default:
          return true;
      }
    },
    metadata: booleanMetadata(true),
  },
  isServiceStartDateVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue, isMobile }) => {
      if (isMobile) {
        return true;
      }
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return false;
        default:
          return true;
      }
    },
    metadata: booleanMetadata(true),
  },
  isCourseAvailabilityVisible: booleanParam(true),
  isServiceDurationVisible: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue, isMobile }) => {
      if (isMobile) {
        return true;
      }
      const preset = getPresetId(getStyleParamValue);
      switch (preset) {
        case CLASSIC_PRESET_ID:
        case CLASSIC_EDITOR_X_PRESET_ID:
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return false;
        default:
          return true;
      }
    },
    metadata: booleanMetadata(true),
  },
  isServicePriceVisible: booleanParam(true),
  isBookButtonVisible: booleanParam(true),
  isExplorePlansVisible: booleanParam(true),
  showBreadcrumbs: booleanParam(false),

  widgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  widgetTitleFont: fontParam(
    'Page-title',
    { size: 32, htmlTag: 'h1' },
    CssFont.WstHeading2Font,
  ),
  mobileWidgetTitleFontSize: numberParam(StyleType.fontSize, 24),
  mobilewidgetMenuTitleFontSize: numberParam(StyleType.fontSize, 20),
  widgetTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  widgetMenuTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  widgetMenuTitleFont: fontParam(
    'Page-title',
    { size: 24, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  infoBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  borderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  borderWidth: numberParam(StyleType.borderWidth, 1),
  cornerRadius: numberParam(StyleType.borderRadius, 0),
  filterTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  titleAlignment: stringParam<AlignmentOptions>(
    undefined,
    'AlignmentOptions: center | left | right',
  ),
  menuTitleAlignment: stringParam(
    AlignmentOptions.CENTER,
    'AlignmentOptions: center | left | right',
  ),
  filterTitleFont: fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
  serviceTextAlignment: stringParam<AlignmentOptions>(
    undefined,
    'AlignmentOptions: center | left | right',
  ),
  mobileFilterTitleFontSize: numberParam(StyleType.fontSize, 14),
  filterSelectedIndicationColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  filterSelectedTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  filterSelectedBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  filterHoverIndicationColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  filterHoverTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  filterHoverBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.6,
    CssColor.WstSystemLine1Color,
  ),
  filterBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  filterBorderWidth: numberParam(StyleType.borderWidth, 1),
  filterBorderRadius: numberParam(StyleType.borderRadius, 0),
  filterBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  filterSpacing: numberParam(StyleType.padding, 8),
  dropdownTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  dropdownBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.4,
    CssColor.WstSystemLine1Color,
  ),
  dropdownBorderWidth: numberParam(StyleType.borderWidth, 1),
  dropdownBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  dropdownTextFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  serviceImageBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-5',
    0.2,
    CssColor.WstSecondaryBackgroundColor,
  ),
  maxCardsPerRow: numberParam(StyleType.width, undefined),
  cardSpacing: numberParam(StyleType.padding, undefined),
  gridCardsSpacing: numberParam(StyleType.padding, undefined),
  stripCardSpacing: numberParam(StyleType.padding, undefined),
  stripCardColumnsSpacing: numberParam(StyleType.padding, 40),
  menuSectionsSpacing: numberParam(StyleType.padding, 80),
  overlappingSidePadding: numberParam(StyleType.padding, 60),
  overlappingVerticalPadding: numberParam(StyleType.padding, 60),
  stripBorderWidth: numberParam(StyleType.borderWidth, 0),
  stripSidePadding: {
    inheritDesktop: false,
    ...numberParam(StyleType.padding, 0),
  },
  stripVerticalPadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 32),
  },
  classicSidePadding: numberParam(StyleType.padding, 60),
  classicVerticalPadding: numberParam(StyleType.padding, 60),
  gridSidePadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 32),
  },
  gridVerticalPadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 32),
  },
  imageAndTextRatio: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      const preset = getPresetId(getStyleParamValue);

      switch (preset) {
        case CLASSIC_PRESET_ID:
        case CLASSIC_EDITOR_X_PRESET_ID:
          return Number(ImageAndTextRatioOptions.RATIO_40_60);
        default:
          return Number(ImageAndTextRatioOptions.RATIO_50_50);
      }
    },
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      defaultValue: {
        type: 'static',
        value: Number(ImageAndTextRatioOptions.RATIO_50_50),
      },
      ai: {
        description:
          'ImageAndTextRatioOptions: 50 (50:50), 40 (40:60), 30 (30:70)',
      },
    },
  },
  servicesDividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  serviceNameFont: fontParam(
    'Page-title',
    { size: 24, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  serviceNameColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobileServiceNameFontSize: numberParam(StyleType.fontSize, 20),
  serviceTagLineFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  serviceTaglineColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  mobileServiceTaglineFontSize: numberParam(StyleType.fontSize, 14),
  moreInfoButtonColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  moreInfoButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  mobileMoreInfoButtonFontSize: numberParam(StyleType.fontSize, 14),
  serviceDividerWidth: numberParam(StyleType.borderWidth, 1),
  servciesDividerWidthInStrip: numberParam(StyleType.borderWidth, undefined),
  serviceDividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  serviceDetailsColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  serviceDetailsFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  priceAfterDiscountFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  priceAfterDiscountColor: colorParam(
    StyleType.color,
    'color-3',
    CssColor.WstSystemDisabledColor,
  ),
  discountNameFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  discountNameColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  mobileDiscountPriceFontSize: numberParam(StyleType.fontSize, 14),
  mobileDiscountNameFontSize: numberParam(StyleType.fontSize, 14),
  courseAvailabilityColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  courseAvailabilityFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  mobileServiceDetailsFontSize: numberParam(StyleType.fontSize, 14),
  bookButtonFont: fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
  mobileBookButtonFontSize,
  bookButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  bookButtonHoverBackgroundColor: colorRefParam(
    bookButtonBackgroundColor,
    'bookButtonBackgroundColor',
    CssColor.WstPrimaryBackgroundColor,
  ),
  holeBookButtonTextColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  fillBookButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonHoverTextColor: colorRefParam(
    fillBookButtonTextColor,
    'fillBookButtonTextColor',
    CssColor.WstButtonPrimaryColor,
  ),
  bookButtonBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstSystemLine1Color,
  ),
  bookButtonHoverBorderColor: colorRefParam(
    bookButtonBorderColor,
    'bookButtonBorderColor',
    CssColor.WstSystemLine1Color,
  ),
  bookButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  bookButtonCornerRadius: numberParam(StyleType.borderRadius, 100),
  bookButtonBorderRadius: numberParam(StyleType.borderRadius, 0),
  explorePlansTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  explorePlansFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  mobileExplorePlansFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(mobileBookButtonFontSize) ?? 14,
    metadata: {
      type: StyleType.fontSize,
      defaultValue: {
        type: 'ref',
        ref: 'mobileBookButtonFontSize',
        fallback: {
          type: 'static',
          value: 14,
        },
      },
    },
  },
  serviceImageCornerRadius: numberParam(StyleType.borderRadius, 0),
  serviceImagePadding: numberParam(StyleType.padding, 0),
  fillLoadServicesButtonTextColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  loadServicesButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  loadServicesButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  loadServicesButtonBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  loadServicesButtonBorderRadius: numberParam(StyleType.borderRadius, 0),
  loadServicesButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  serviceImageShape: stringParam<ImageShapeOptions>(
    undefined,
    'ImageShapeOptions: square | round | rectangle',
  ),
  serviceListLayout: {
    inheritDesktop: false,
    ...customEnumParam<ServiceListLayoutOptions>(undefined),
  },
  serviceListMobileLayout: customEnumParam<ServiceListLayoutOptions>(undefined),
  serviceImagePosition: stringParam<ImagePositionOptions>(
    undefined,
    'ImagePositionOptions: left | right | invalid left | invalid right',
  ),
  alternateImageInfoSides: booleanParamWithUndefinedLegacy(false),
  numberOfColumns: numberParam(StyleType.width, 1),
  serviceImageSize: {
    inheritDesktop: false,
    ...stringParam<ImageSizeOptions>(
      undefined,
      'ImageSizeOptions: small | medium | large',
    ),
  },
  /* legacy keys app settings*/
  OFFERING_NAME_FONT: legacyFontParam(),
  CATEGORY_NAME_FONT: legacyFontParam(),
  MULTI_OFFERINGS_TITLE_FONT: legacyFontParam(),
  MOBILE_CATEGORY_NAME_FONT: legacyFontParam(),
  COURSE_AVAILABILITY_FONT: legacyFontParam(),
  OFFERING_TAGLINE_FONT: legacyFontParam(),
  OFFERING_MORE_INFO_LABEL_FONT: legacyFontParam(),
  OFFERING_DETAILS_FONT: legacyFontParam(),
  BUTTON_TEXT_FONT: legacyFontParam(),
  EXPLORE_PLANS_FONT: legacyFontParam(),

  /* end legacy keys app settings*/
});

export default stylesParams;

// noinspection JSUnusedGlobalSymbols
export const customCssVars: CustomCssVarsFn = (args) => {
  const { styleParams, tpaData, isMobile } = args;

  const displayStyle = (
    booleanStyleParam: VisibilityKey,
    displayType: 'block' | 'flex' = 'block',
  ) => (styleParams.booleans[booleanStyleParam] ? displayType : 'none');

  const isServiceImageVisible =
    styleParams.booleans.isServiceImageVisible ??
    getIsServiceImageVisibleFromTpaDate(tpaData, isMobile);

  return {
    serviceImageDisplay: isServiceImageVisible ? 'block' : 'none',
    isServiceImageVisible: isServiceImageVisible ? 1 : 0,
    titleDisplay: displayStyle('isTitleVisible'),
    serviceDividerDisplay: displayStyle('isServiceDividerVisible'),
    onlineBadgeDisplay: displayStyle('isOnlineBadgeVisible'),
    tagLineDisplay: displayStyle('isTagLineVisible'),
    moreInfoButtonDisplay: displayStyle('isMoreInfoButtonVisible'),
    serviceOfferedDaysDisplay: displayStyle('isServiceOfferedDaysVisible'),
    serviceStartDateDisplay: displayStyle('isServiceStartDateVisible'),
    courseAvailabilityDisplay: displayStyle('isCourseAvailabilityVisible'),
    serviceDurationDisplay: displayStyle('isServiceDurationVisible'),
    servicePriceDisplay: displayStyle('isServicePriceVisible'),
    bookButtonDisplay: displayStyle('isBookButtonVisible'),
    explorePlansDisplay: displayStyle('isExplorePlansVisible'),
    breadcrumbsDisplay: styleParams.booleans.showBreadcrumbs ? 'block' : 'none',
    priceAfterDiscountDisplay: displayStyle('isPriceAfterDiscountVisible'),
    serviceDiscountNameDisplay: displayStyle('isDiscountNameVisible', 'flex'),
    ...generateAllCssVars(args),
  };
};
